import type { AfCalendarConfig } from '../../types/appointments.js';

// The Anytime Fitness calendar form's pure rules (shared by create and edit, tested without jsdom).

export interface AfFormDraft {
  locationNumber: string;
  /** Comma / space / newline separated AF staff ids. Empty = anyone AF lists. */
  allowlistText: string;
  testMode: boolean;
}

export const emptyAfDraft = (): AfFormDraft => ({ locationNumber: '', allowlistText: '', testMode: true });

export function parseStaffAllowlist(text: string): string[] {
  return [...new Set(text.split(/[\s,;]+/).map((t) => t.trim()).filter(Boolean))];
}

export function afDraftFrom(config: AfCalendarConfig): AfFormDraft {
  return { locationNumber: config.locationNumber, allowlistText: (config.staffAllowlist ?? []).join(', '), testMode: config.testMode };
}

/** The club id error, or null. Mirrors the server's schema (AU-/NZ- followed by digits). */
export function afClubError(locationNumber: string): string | null {
  return /^(AU|NZ)-\d+$/.test(locationNumber.trim().toUpperCase()) ? null : 'Enter the Anytime Fitness club id, like AU-1432.';
}

export function afConfigFromDraft(draft: AfFormDraft): AfCalendarConfig {
  const staffAllowlist = parseStaffAllowlist(draft.allowlistText);
  return {
    locationNumber: draft.locationNumber.trim().toUpperCase(),
    ...(staffAllowlist.length > 0 ? { staffAllowlist } : {}),
    testMode: draft.testMode,
  };
}

/** The sentence under the test-mode tick, so nobody is surprised by what it refuses. */
export function testModeExplanation(draft: Pick<AfFormDraft, 'locationNumber' | 'testMode'>): string {
  if (!draft.testMode) return 'Live: bookings go straight into this club\'s Anytime Fitness calendar.';
  const club = draft.locationNumber.trim().toUpperCase();
  return `Test mode: only the bot's test phone numbers can book, into ${club || 'this club'}'s own ClubOS calendar. Those test visits will appear in ClubOS; staff cancel them there.`;
}
